import React, { Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ClientAuthProvider } from "@/context/ClientAuthContext";
import AuthCallback from "@/components/AuthCallback";
import ClientLogin, { ClientRegister, ClientVerify, ClientReset } from "@/pages/account/ClientAuth";
import ClientDashboard from "@/pages/account/ClientDashboard";

import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Destinations from "@/pages/Destinations";
import Hotels from "@/pages/Hotels";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import LegalPage from "@/pages/LegalPage";
import ServiceEnquiry from "@/pages/ServiceEnquiry";
import VisaForm from "@/pages/VisaForm";
import FlightForm from "@/pages/FlightForm";
import HolidayForm from "@/pages/HolidayForm";
import HolidayPackages from "@/pages/HolidayPackages";
import Tours from "@/pages/Tours";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import PayPage from "@/pages/PayPage";
import PaymentResult from "@/pages/PaymentResult";

const AdminLogin = React.lazy(() => import("@/pages/admin/Login"));
const AdminLayout = React.lazy(() => import("@/components/AdminLayout"));
const Dashboard = React.lazy(() => import("@/pages/admin/Dashboard"));
const Inquiries = React.lazy(() => import("@/pages/admin/Inquiries"));
const InquiryDetail = React.lazy(() => import("@/pages/admin/InquiryDetail"));
const VisaTracking = React.lazy(() => import("@/pages/admin/VisaTracking"));
const Payments = React.lazy(() => import("@/pages/admin/Payments"));
const Clients = React.lazy(() => import("@/pages/admin/Clients"));
const Reports = React.lazy(() => import("@/pages/admin/Reports"));
const Tasks = React.lazy(() => import("@/pages/admin/Tasks"));
const Staff = React.lazy(() => import("@/pages/admin/Staff"));
const Content = React.lazy(() => import("@/pages/admin/Content"));

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#0A2E4D]">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (roles && !roles.includes(user.role) && user.role !== "admin") return <Navigate to="/admin" replace />;
  return children;
}

function RedirectPreserveQuery({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}


function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

function AccountEntry() {
  if (window.location.hash && window.location.hash.includes("session_id=")) return <AuthCallback />;
  return <ClientDashboard />;
}

export default function App() {
  useEffect(() => { initAnalytics(); }, []);
  return (
    <AuthProvider>
      <ClientAuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <RouteTracker />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-[#0A2E4D]">Loading…</div>}>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/account" element={<AccountEntry />} />
            <Route path="/account/login" element={<ClientLogin />} />
            <Route path="/account/register" element={<ClientRegister />} />
            <Route path="/account/verify" element={<ClientVerify />} />
            <Route path="/account/reset" element={<ClientReset />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/destinations" element={<Destinations />} />
            <Route path="/hotels" element={<Hotels />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/corporate" element={<ServiceEnquiry type="corporate" />} />
            <Route path="/insurance" element={<ServiceEnquiry type="insurance" />} />
            <Route path="/transfers" element={<ServiceEnquiry type="transfers" />} />
            <Route path="/visa" element={<VisaForm />} />
            <Route path="/visas" element={<RedirectPreserveQuery to="/visa" />} />
            <Route path="/flights" element={<FlightForm />} />
            <Route path="/holidays" element={<HolidayPackages />} />
            <Route path="/holidays/request" element={<HolidayForm />} />
            <Route path="/tours" element={<Tours />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pay/:token" element={<PayPage />} />
            <Route path="/payment/result" element={<PaymentResult />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="inquiries" element={<Inquiries />} />
            <Route path="inquiries/:id" element={<InquiryDetail />} />
            <Route path="visa-tracking" element={<VisaTracking />} />
            <Route path="payments" element={<ProtectedRoute roles={["admin", "manager", "accountant"]}><Payments /></ProtectedRoute>} />
            <Route path="clients" element={<Clients />} />
            <Route path="reports" element={<Reports />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="staff" element={<ProtectedRoute roles={["admin"]}><Staff /></ProtectedRoute>} />
            <Route path="content" element={<ProtectedRoute roles={["admin", "manager"]}><Content /></ProtectedRoute>} />
          </Route>

        </Routes>
        </Suspense>
      </BrowserRouter>
      </ClientAuthProvider>
    </AuthProvider>
  );
}
